import { useTranslation } from 'react-i18next';
import {
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/atoms/dialog';
import { Button } from '@/components/atoms/button';
import { ChevronLeft, Loader2 } from 'lucide-react';
import { StepIndicator } from './StepIndicator';
import { VariantConfigItem } from './VariantConfigItem';
import type { VariationType, Product, CreateProductGroupItemDto } from '@/lib/types';

interface ConfigureVariantsStepProps {
  currentProduct: Product;
  currentProductId: string;
  selectedProductIds: string[];
  availableProducts: Product[];
  variantConfigs: Record<string, CreateProductGroupItemDto>;
  isUpdating: boolean;
  variationType: VariationType;
  onUpdateVariantConfig: (productId: string, field: keyof CreateProductGroupItemDto, value: string | number | boolean) => void;
  onBack: () => void;
  onSubmit: () => void;
}

export function ConfigureVariantsStep({
  currentProduct,
  currentProductId,
  selectedProductIds,
  availableProducts,
  variantConfigs,
  isUpdating,
  variationType,
  onUpdateVariantConfig,
  onBack,
  onSubmit,
}: ConfigureVariantsStepProps) {
  const { t } = useTranslation();

  const allProducts = [
    currentProduct,
    ...availableProducts.filter((p) => selectedProductIds.includes(p.refId || p.id)),
  ];

  return (
    <>
      <DialogHeader>
        <DialogTitle>{t('associations.configureVariants')}</DialogTitle>
        <DialogDescription>{t('associations.configureVariantsDescription')}</DialogDescription>
      </DialogHeader>

      <StepIndicator step="configure_variants" variationType={variationType} />

      <div className="space-y-4 max-h-[400px] overflow-y-auto">
        {allProducts.map((product, index) => {
          const productId = product.refId || product.id;
          const config = variantConfigs[productId] || {};

          return (
            <VariantConfigItem
              key={productId}
              product={product}
              productId={productId}
              currentProductId={currentProductId}
              config={config}
              variationType={variationType}
              index={index}
              onUpdateConfig={onUpdateVariantConfig}
            />
          );
        })}
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onBack}>
          <ChevronLeft className="size-4 mr-1" />
          {t('common.back')}
        </Button>
        <Button onClick={onSubmit} disabled={isUpdating}>
          {isUpdating && <Loader2 className="size-4 mr-2 animate-spin" />}
          {t('common.confirm')}
        </Button>
      </DialogFooter>
    </>
  );
}
